const entities = { amp: '&', nbsp: ' ', lt: '<', gt: '>', quot: '"', apos: "'" };
const featureKey = value => value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
const unique = entries => [...new Map(entries.filter(Boolean).map(value => [featureKey(value), value])).values()];

/** Extract configured copy as text only; it never becomes executable HTML. */
export function catalogDescriptionFeatures(value) {
  if (typeof value !== 'string') return [];
  const text = value
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style|iframe|object|template)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<\s*\/?\s*(?:b|li|p|div|br|ul|ol)\b[^>]*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&(#x[\da-f]+|#\d+|amp|nbsp|lt|gt|quot|apos);/gi, (source, name) => {
      if (!name.startsWith('#')) return entities[name.toLowerCase()] ?? source;
      const code = name[1].toLowerCase() === 'x' ? parseInt(name.slice(2), 16) : Number(name.slice(1));
      return code > 0 && code <= 0x10ffff && !(code >= 0xd800 && code <= 0xdfff) ? String.fromCodePoint(code) : source;
    });
  return unique(text.split(/\r?\n|\|/).map(line => line.replace(/\s+/g, ' ').trim()));
}

/** Only this SKU's configured rights/copy; no inferred counts or tier inheritance. */
export function catalogPlan(plan) {
  const rights = Array.isArray(plan.rights)
    ? unique(plan.rights.flatMap(value => catalogDescriptionFeatures(typeof value === 'string' ? value : value?.desc))) : [];
  const comparison = catalogComparisonFeatures(plan);
  return { ...plan, comparison_features: comparison, features: comparison ? comparison.map(row => row.text) : rights.length ? rights : catalogDescriptionFeatures(plan.vip_desc) };
}

/** The complete configured list is optional during the old-server transition. */
export function catalogComparisonFeatures(plan) {
  const rows = plan?.comparison_features;
  if (!Array.isArray(rows) || rows.length === 0 || rows.length > 40) return undefined;
  const codes = new Set();
  for (const row of rows) {
    if (!row || typeof row.code !== 'string' || !/^[a-z][a-z0-9_]{0,79}$/.test(row.code)
      || codes.has(row.code) || typeof row.text !== 'string' || !row.text.trim() || row.text.length > 8192) return undefined;
    codes.add(row.code);
  }
  return rows.map(({ code, text }) => ({ code, text }));
}

export function catalogPrice(value, locale) {
  const amount = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
  return Number.isFinite(amount) && amount >= 0
    ? new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', minimumFractionDigits: amount % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(amount)
    : '—';
}

export function catalogFeatures(plans) {
  return unique(plans.flatMap(plan => plan.features));
}

export function catalogIncludes(plan, feature) {
  return plan.features.some(value => featureKey(value) === featureKey(feature));
}
